import { ReleaseNotesQueryParams, SemanticVersion } from './types';

/**
 * Matches the markdown of an h2 with semantic version number.
 * Captures the full heading text (version + any additional text).
 * @example: ## 1.7.4 (4. Dezember 2025)
 * @example: ## 1.7.4 (hotfix)
 * @example: ## 1.7.4
 */
const MARKDOWN_VERSION_HEADING_REGEX = /^##\s+(\d+\.\d+(?:\.\d+)?.*)$/gm;

/**
 * Extracts the semantic version from a heading string.
 */
const SEMVER_REGEX = /^(\d+\.\d+(?:\.\d+)?)/;

/**
 * Matches the opening line of a `> [!SUMMARY]` alert blockquote.
 */
const SUMMARY_ALERT_REGEX = /^>\s*\[!\s*SUMMARY\s*\]/i;

/**
 * Generates the file name for the release notes to be fetched from the API.
 * @param app - The application to get the release notes for. Defaults to 'customer-portal'.
 * @param environment - The environment to get the release notes for. Defaults to 'production'.
 * @param language - The language to get the release notes for. Defaults to 'de'.
 * @returns The file name for the release notes as a string.
 */
export function getReleaseNotesFileName({
  app = 'customer-portal',
  environment = 'production',
  language = 'de',
}: Pick<ReleaseNotesQueryParams, 'app' | 'environment' | 'language'>) {
  return `vestigas-${app}_release-notes_${environment}_${language}.md`;
}

/**
 * Compares two semantic versions.
 * @param v1 - First version string (e.g., "2.4.0")
 * @param v2 - Second version string (e.g., "2.3.7")
 * @returns Positive number if v1 > v2, negative if v1 < v2, 0 if equal
 */
export function compareVersions(v1: string, v2: string | undefined): number {
  if (!v2) {
    return 1;
  }

  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] ?? 0;
    const part2 = parts2[i] ?? 0;

    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }

  return 0;
}

/**
 * Extracts versions and their content from release notes markdown.
 * Filters text to only include versions that match the version and comparator (e.g. >= 1.0.0).
 * @param markdown - The markdown content to extract releases from.
 * @param version - The minimum version to return, or 'latest' to get the latest version.
 * @param comparator - The comparator to use to filter releases. Defaults to '=' when version is 'latest'.
 * @returns The filtered releases as markdown.
 */
export function extractReleases({
  markdown,
  version,
  comparator,
}: {
  markdown: string;
  version?: SemanticVersion | 'latest';
  comparator: ReleaseNotesQueryParams['comparator'];
}) {
  // Split by H2 headings (## version) - supports both 2-part (2.0) and 3-part (2.0.0) versions
  const sections = markdown.split(MARKDOWN_VERSION_HEADING_REGEX);

  // First section is the H1 title and any content before first version
  const header = sections[0]?.trim() ?? '';

  // Collect all versions and their content
  const versionEntries: Array<{
    content: string;
    heading: string;
    version: string;
  }> = [];

  // Process pairs: [heading, content, heading, content, ...]
  for (let i = 1; i < sections.length; i += 2) {
    const heading = sections[i]?.trim() ?? '';
    const version = heading.match(SEMVER_REGEX)?.[1] ?? '';

    const content = sections[i + 1]?.trim() ?? '';

    if (!version) {
      continue;
    }

    versionEntries.push({
      content,
      heading,
      version,
    });
  }

  // Resolve effective version
  let effectiveVersion: SemanticVersion | undefined;
  let effectiveComparator = comparator;

  if (version === 'latest') {
    if (versionEntries.length === 0) {
      return '';
    }

    // Find the latest version by comparing all versions
    effectiveVersion = versionEntries.reduce((latest, current) => {
      return compareVersions(current.version, latest) > 0
        ? current.version
        : latest;
    }, versionEntries[0].version) as SemanticVersion;

    // Default to '=' when version is 'latest', but allow override
    effectiveComparator = comparator ?? '=';
  } else if (version) {
    effectiveVersion = version;
  }

  const releases: string[] = [];

  // Apply filtering based on comparator
  for (const { content, heading, version } of versionEntries) {
    const releaseContent = `## ${heading}\n\n${content}`;

    if (effectiveVersion === undefined) {
      // Unfiltered full list of release notes
      releases.push(releaseContent);
      continue;
    }

    switch (effectiveComparator) {
      case '>=':
        if (compareVersions(version, effectiveVersion) >= 0) {
          releases.push(releaseContent);
        }
        break;

      case '>':
        if (compareVersions(version, effectiveVersion) > 0) {
          releases.push(releaseContent);
        }
        break;

      case '=':
        if (compareVersions(version, effectiveVersion) === 0) {
          releases.push(releaseContent);
        }
        break;

      case '<':
        if (compareVersions(version, effectiveVersion) < 0) {
          releases.push(releaseContent);
        }
        break;

      case '<=':
        if (compareVersions(version, effectiveVersion) <= 0) {
          releases.push(releaseContent);
        }
        break;

      default:
        // Unfiltered full list of release notes
        releases.push(releaseContent);
        break;
    }
  }

  if (releases.length === 0) {
    return '';
  }

  return releases.join('\n\n');
}

/**
 * Extracts the `[!SUMMARY]` alert blockquote of a single release, i.e. the
 * blockquote lines from the alert up to the first blank line, so that a later
 * alert of the release is not pulled in.
 */
function extractSummary(releaseContent: string) {
  const lines = releaseContent.split(/\r?\n/);
  const alertStartIndex = lines.findIndex((line) =>
    SUMMARY_ALERT_REGEX.test(line.trim()),
  );

  if (alertStartIndex === -1) {
    return '';
  }

  const summaryLines: string[] = [];

  for (const line of lines.slice(alertStartIndex)) {
    if (!line.trim().startsWith('>')) {
      break;
    }

    summaryLines.push(line);
  }

  return summaryLines.join('\n');
}

/**
 * Finds the release to tell the user about in release notes markdown: the
 * highest version that has a summary to show. Version and summary always come
 * from the same release, so nothing can show one release's text under another
 * release's version.
 *
 * @param markdown - Release notes markdown, typically already filtered to the
 *   releases newer than the running version.
 * @returns The version and its summary, or `null` when no release has anything
 *   to say.
 */
export function getNewestAnnouncedRelease(markdown: string) {
  const sections = markdown.split(MARKDOWN_VERSION_HEADING_REGEX);

  let newestRelease: { summary: string; version: string } | null = null;

  // Process pairs: [heading, content, heading, content, ...]
  for (let i = 1; i < sections.length; i += 2) {
    const version = sections[i]?.trim().match(SEMVER_REGEX)?.[1];
    const summary = extractSummary(sections[i + 1] ?? '');

    if (!version || !summary) {
      continue;
    }

    if (!newestRelease || compareVersions(version, newestRelease.version) > 0) {
      newestRelease = { summary, version };
    }
  }

  return newestRelease;
}
